// src/features/files/components/PdfCanvasViewer.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

// pdf.js (Mozilla's prebuilt browser build) is vendored in public/pdfjs/ and
// loaded on first use, so only browsers without a PDF viewer download it.
// It is not an npm dependency: the npm package drags in the native `canvas`
// module, which breaks jsdom under Jest, and bundling its worker runs Babel
// over it. Upgrade: copy legacy/build/pdf.min.js and pdf.worker.min.js from
// the new pdfjs-dist release into public/pdfjs/ and change PDFJS_VERSION.
const PDFJS_VERSION = '3.11.174';
const PDFJS_BASE = `${process.env.PUBLIC_URL}/pdfjs`;

let pdfjsPromise = null;
function loadPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `${PDFJS_BASE}/pdf-${PDFJS_VERSION}.min.js`;
      script.async = true;
      script.onload = () => {
        const pdfjs = window.pdfjsLib;
        pdfjs.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}/pdf.worker-${PDFJS_VERSION}.min.js`;
        resolve(pdfjs);
      };
      script.onerror = () => { pdfjsPromise = null; reject(new Error('pdf.js failed to load')); };
      document.head.appendChild(script);
    });
  }
  return pdfjsPromise;
}

/**
 * Browsers without a built-in PDF viewer (Chrome on Android) show only an
 * "Open" button for an embedded PDF. Use this there instead of <embed>.
 */
export const hasNativePdfViewer = () => navigator.pdfViewerEnabled !== false;

/**
 * Draws every page of a PDF onto canvases with pdf.js, stacked to scroll.
 * Pages are drawn one after the other, so the first shows quickly; they are
 * sized to the container's width at the screen's pixel density, so text stays
 * sharp and pinch zoom works like on any page.
 */
export function PdfCanvasViewer({ url, title, background = 'transparent' }) {
  const { t } = useTranslation('files');
  const containerRef = useRef(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    const container = containerRef.current;
    if (!url || !container) return undefined;
    let cancelled = false;
    let task = null;
    setState('loading');
    container.replaceChildren();

    loadPdfjs()
      .then((pdfjs) => {
        if (cancelled) return null;
        task = pdfjs.getDocument({ url, withCredentials: false });
        return task.promise;
      })
      .then(async (pdf) => {
        if (!pdf) return;
        const width = container.clientWidth || window.innerWidth;
        const ratio = window.devicePixelRatio || 1;
        for (let n = 1; n <= pdf.numPages && !cancelled; n += 1) {
          const page = await pdf.getPage(n);
          const viewport = page.getViewport({ scale: (width / page.getViewport({ scale: 1 }).width) * ratio });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = '100%';
          canvas.style.display = 'block';
          canvas.style.marginBottom = '8px';
          canvas.style.background = '#fff';
          canvas.setAttribute('aria-label', `${title ?? 'PDF'} ${n}/${pdf.numPages}`);
          container.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          if (n === 1 && !cancelled) setState('ready');
        }
      })
      .catch(() => { if (!cancelled) setState('error'); });

    return () => {
      cancelled = true;
      task?.destroy();
    };
  }, [url, title]);

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', bgcolor: background, p: 1 }}>
      {state === 'loading' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      )}
      {state === 'error' && <Alert severity="error">{t('files:preview_failed')}</Alert>}
      <Box ref={containerRef} data-testid="pdf-canvas-viewer" />
    </Box>
  );
}
